<template>
  <modal-vue
    @close="handleToggleModal"
    :modalActive="modals[modalName]"
    max-width="!w-[80vw] !max-w-[960px]"
    min-height="90vh"
    max-height="90vh"
    class="figma-custom-modal"
    :has-header-border="true"
  >
    <template #header>
      <p class="font-bold text-lg text-center text-textPrimary">Aplicar Filtros</p>
    </template>

    <div class="flex flex-col md:flex-row flex-1 min-h-0">
      <nav
        class="shrink-0 flex flex-row md:flex-col md:w-[260px] bg-[#F0F0F4] border-b md:border-b-0 md:border-r border-[#D8D8E2] overflow-x-auto md:overflow-x-visible"
      >
        <button
          v-for="tab in tabConfig"
          :key="tab.key"
          type="button"
          :data-testid="`filter-tab-${tab.key}-button`"
          @click="currentTab = tab.key"
          class="flex items-center gap-2 md:gap-4 px-3 md:px-4 py-3 md:py-4 text-left transition-colors duration-200"
          :class="
            currentTab === tab.key
              ? 'bg-[#2F7F91] text-white font-bold'
              : 'text-[#16161D] hover:bg-[#E8E8EE]'
          "
        >
          <v-icon
            size="24"
            :icon="tab.icon"
            :color="currentTab === tab.key ? 'white' : '#16161D'"
          />
          <span class="text-xs md:text-base leading-6 whitespace-nowrap">{{ tab.label }}</span>
        </button>
      </nav>

      <div class="flex-1 flex flex-col min-w-0">
        <div class="flex-1 overflow-y-auto p-4 md:p-6">
          <h2 class="font-bold text-xl leading-6 text-[#323232] mb-6 text-left">
            {{ currentTabLabel }}
          </h2>

          <!-- Informações Principais -->
          <div
            v-show="currentTab === 'info'"
            class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"
          >
            <div class="md:col-span-2">
              <MultipleSchedules
                v-model:company="filterStates.company"
                v-model:healthUnits="filterStates.healthUnits"
                v-model:schedules="filterStates.schedules"
                v-model:doctors="filterStates.doctors"
                v-model:positions="filterStates.positions"
                v-model:shifts="filterStates.shifts"
                v-model:isOpenDoctorStation="filterStates.isOpenDoctorStation"
                :hide-open-doctor-station="!!consultingCoverageCounts"
              />
            </div>
            <v-autocomplete
              v-model="filterStates.operationalGroups"
              :items="operationalGroupOptions"
              label="Grupo Operacional"
              multiple
              clearable
              theme="light"
              variant="outlined"
              density="compact"
              class="w-full md:col-span-2"
              color="primary20"
            />
            <StationSectorSpecialtyFilters v-if="showSectorSpecialty" />
            <v-text-field
              v-model="filterStates.doctorName"
              class="w-full md:col-span-2"
              label="Nome do Médico"
              placeholder="Buscar por nome..."
              clearable
              color="primary20"
              variant="outlined"
              density="compact"
            />
            <v-text-field
              v-model="tempCrms"
              class="w-full"
              label="CRM (números)"
              placeholder="Ex: 12345, 67890"
              clearable
              color="primary20"
              variant="outlined"
              density="compact"
              hint="Números de CRM separados por vírgula"
            />
            <v-text-field
              v-model="tempCrmsUf"
              class="w-full"
              label="UF do CRM"
              placeholder="Ex: SP, RJ"
              clearable
              color="primary20"
              variant="outlined"
              density="compact"
              hint="Estados separados por vírgula"
            />
          </div>

          <!-- Data e Recorrência -->
          <div
            v-show="currentTab === 'data'"
            class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"
          >
            <DatePicker
              v-model="filterStates.startDate"
              locale="pt-BR"
              :masks="{ input: `DD/MM/YYYY` }"
            >
              <template #default="{ inputValue, inputEvents }">
                <v-text-field
                  label="Dia Inicial"
                  placeholder="DD/MM/AAAA"
                  :prepend-inner-icon="mdiCalendar"
                  :model-value="inputValue"
                  v-on="inputEvents"
                  density="compact"
                  variant="outlined"
                  class="w-full"
                  color="primary20"
                />
              </template>
            </DatePicker>
            <DatePicker
              v-model="filterStates.endDate"
              locale="pt-BR"
              :masks="{ input: `DD/MM/YYYY` }"
            >
              <template #default="{ inputValue, inputEvents }">
                <v-text-field
                  label="Dia Final"
                  placeholder="DD/MM/AAAA"
                  :prepend-inner-icon="mdiCalendar"
                  :model-value="inputValue"
                  v-on="inputEvents"
                  density="compact"
                  variant="outlined"
                  class="w-full"
                  color="primary20"
                />
              </template>
            </DatePicker>
            <v-text-field
              v-model="tempStartHour"
              class="w-full"
              type="time"
              label="Hora Início"
              color="primary20"
              variant="outlined"
              density="compact"
            />
            <v-text-field
              v-model="tempEndHour"
              class="w-full"
              type="time"
              label="Hora Fim"
              color="primary20"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.weekDays"
              multiple
              class="w-full md:col-span-2"
              color="primary20"
              label="Dias da semana"
              :items="weekDays"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.weekOfMonth"
              :items="weekOfMonthOptions"
              label="Semana do Mês"
              multiple
              class="w-full"
              color="primary20"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.stationWeekParity"
              :items="parityOptions"
              label="Paridade da Semana"
              hint="Semana de domingo a sábado: o sábado e o domingo de um mesmo fim de semana ficam em paridades diferentes."
              persistent-hint
              class="w-full"
              color="primary20"
              variant="outlined"
              density="compact"
            />
          </div>

          <!-- Valores e Pagamento -->
          <div
            v-show="currentTab === 'valores'"
            class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"
          >
            <v-text-field
              v-model="filterStates.valueMin"
              class="w-full"
              prefix="R$"
              type="number"
              label="Valor Mínimo (Vaga)"
              color="primary20"
              variant="outlined"
              density="compact"
              hint="Valor da vaga"
            />
            <v-text-field
              v-model="filterStates.valueMax"
              class="w-full"
              prefix="R$"
              type="number"
              label="Valor Máximo"
              color="primary20"
              variant="outlined"
              density="compact"
              hint="Valor da vaga"
            />
            <v-text-field
              v-model="filterStates.paymentValueMin"
              class="w-full"
              prefix="R$"
              type="number"
              label="Valor aprovado mínimo"
              color="primary20"
              variant="outlined"
              density="compact"
              hint="Valor aprovado pelo hospital"
            />
            <v-text-field
              class="w-full"
              v-model="filterStates.paymentValueMax"
              prefix="R$"
              type="number"
              label="Valor aprovado máximo"
              color="primary20"
              variant="outlined"
              hint="Valor aprovado pelo hospital"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.advanceRequested"
              class="w-full"
              color="primary20"
              label="Antecipação Solicitada"
              :items="[
                { title: 'Sim', value: true },
                { title: 'Não', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.advancePaid"
              class="w-full"
              color="primary20"
              label="Antecipação Paga"
              :items="[
                { title: 'Sim', value: true },
                { title: 'Não', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.inCash"
              class="w-full"
              color="primary20"
              label="Pagamento á vista"
              :items="[
                { title: 'Sim', value: true },
                { title: 'Não', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
          </div>

          <!-- Status do Plantão -->
          <div
            v-show="currentTab === 'status'"
            class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4"
          >
            <CoverageStatusFilterChips
              v-if="consultingCoverageCounts"
              class="w-full md:col-span-2"
              :model-value="filterStates.coverageStatus ?? []"
              :counts="consultingCoverageCounts"
              @update:model-value="setCoverageStatus"
            />
            <v-autocomplete
              v-if="!consultingCoverageCounts"
              clearable
              v-model="filterStates.openedStation"
              class="w-full"
              color="primary20"
              label="Vaga do plantão"
              :items="[
                { title: 'Não preenchida', value: true },
                { title: 'Preenchida', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.status"
              class="w-full"
              color="primary20"
              label="Status do plantão"
              :items="[
                { title: 'Pendente', value: 'waiting' },
                { title: 'Confirmado', value: 'confirmed' }
              ]"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.stationType"
              class="w-full"
              color="primary20"
              label="Periodicidade"
              :items="stationTypes"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.ourTeam"
              class="w-full"
              color="primary20"
              label="Nosso plantão?"
              :items="stationOur"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.doctorStatus"
              class="w-full"
              color="primary20"
              label="Médico confirmou?"
              :items="[
                { title: 'Confirmado', value: true },
                { title: 'Não confirmado', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.hasException"
              class="w-full"
              color="primary20"
              label="Excepcionalidade"
              :items="[
                { title: 'Com excepcionalidade', value: true },
                { title: 'Sem excepcionalidade', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.withCheckin"
              class="w-full"
              color="primary20"
              label="Checkin"
              :items="[
                { title: 'Com checkin', value: true },
                { title: 'Sem checkin', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
            <v-autocomplete
              clearable
              v-model="filterStates.withCheckout"
              class="w-full"
              color="primary20"
              label="Checkout"
              :items="[
                { title: 'Com checkout', value: true },
                { title: 'Sem checkout', value: false }
              ]"
              variant="outlined"
              density="compact"
            />
          </div>
        </div>

        <div class="shrink-0 px-4 md:px-6 pb-4 md:pb-6 pt-2">
          <div class="flex gap-4 md:gap-6 w-full">
            <ghost-button
              class="flex-1"
              data-testid="filter-clear-button"
              @click="handleClearFilters"
              width="100%"
              text="Limpar filtros"
            />
            <filled-button
              class="flex-1"
              data-testid="filter-apply-button"
              :loading="isLoading"
              @click="handleSubmit"
              text="Aplicar Filtros"
              height="48px"
              width="100%"
            />
          </div>
        </div>
      </div>
    </div>
  </modal-vue>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'
import { filterStates, useFilterStationsStore } from './useFilterStation'
import MultipleSchedules from '@/components/Autocompletes/MultipleSchedules.vue'
import {
  mdiCalendar,
  mdiInformationOutline,
  mdiCalendarClock,
  mdiCashMultiple,
  mdiCheckboxMarkedCircleOutline
} from '@mdi/js'
import { DatePicker } from 'v-calendar'
import { ref, computed, onUnmounted, watch } from 'vue'
import StationSectorSpecialtyFilters from './StationSectorSpecialtyFilters.vue'
import { weekDays } from '@/utils/date'
import { useOperationalGroups } from '@/hooks/useOperationalGroups'
import { useToast } from 'vue-toastification'
import CoverageStatusFilterChips from '@/components/Coverage/CoverageStatusFilterChips.vue'
import { useStationStore } from '@/pinia-store/useStationStore'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'

/**
 * O modal também serve "Aprovar plantões" (Management), cujo endpoint não filtra por setor/
 * especialidade do plantão: os selects (e o fetch dos catálogos) só existem onde a prop liga.
 */
const props = withDefaults(
  defineProps<{ showSectorSpecialty?: boolean; showCoverageStatus?: boolean }>(),
  { showSectorSpecialty: false, showCoverageStatus: false }
)

const tabConfig = [
  { key: 'info', label: 'Informações Principais', icon: mdiInformationOutline },
  { key: 'data', label: 'Data e Recorrência', icon: mdiCalendarClock },
  { key: 'valores', label: 'Valores e Pagamento', icon: mdiCashMultiple },
  { key: 'status', label: 'Status do Plantão', icon: mdiCheckboxMarkedCircleOutline }
]

const currentTab = ref('info')
const currentTabLabel = computed(
  () => tabConfig.find((t) => t.key === currentTab.value)?.label ?? ''
)

const modalName = EModals.FILTER_STATION
const { modals } = storeToRefs(useModalStore())
const modalStore = useModalStore()
const handleToggleModal = () => {
  modalStore.toggleModal(modalName)
}

const isLoading = ref(false)
// Grupos operacionais sob demanda, ao ABRIR o modal: o componente fica montado na página e
// carregar no mount viraria toast a cada visita quando o endpoint nega (ex.: 403 por papel).
// O hook guarda o cache da sessão e divide a busca em voo (um toast só).
const { options: operationalGroups, load: loadOperationalGroups } = useOperationalGroups()
const operationalGroupOptions = computed(() => operationalGroups.value.map((group) => group.value))
const tempStartHour = ref(undefined)
const tempEndHour = ref(undefined)
const tempCrms = ref<string | undefined>(undefined)
const tempCrmsUf = ref<string | undefined>(undefined)

const stationTypes = ref([
  { title: 'Fixo', value: 'FIXO' },
  { title: 'Cobertura', value: 'COBERTURA' },
  { title: 'Extra', value: 'EXTRA' }
])
const stationOur = ref([
  { title: 'Sim', value: true },
  { title: 'Não', value: false }
])

const weekOfMonthOptions = ref([
  { title: 'Semana A', value: 0 },
  { title: 'Semana B', value: 1 },
  { title: 'Semana C', value: 2 },
  { title: 'Semana D', value: 3 },
  { title: 'Semana E', value: 4 }
])
const parityOptions = ref([
  { title: 'Par', value: 0 },
  { title: 'Ímpar', value: 1 }
])

const toast = useToast()

// M10-A: os chips só aparecem quando a última consulta já trouxe `coverageCounts`.
const stationStore = useStationStore()
// Só a Consulta liga (`showCoverageStatus`): "Aprovar plantões" usa este modal e continua como hoje.
const consultingCoverageCounts = computed(() =>
  props.showCoverageStatus ? (stationStore.consulting?.coverageCounts ?? null) : null
)
// Com os chips disponíveis, o toggle legado "Situação da Vaga" (Informações Principais) some: os
// dois juntos eram ambíguos. Um valor legado residual (URL/sessão) vira a seleção equivalente nos
// chips, para nada ficar filtrando escondido. `true` = só vaga aberta (c0f1172b); `false` = preenchida.
watch(
  consultingCoverageCounts,
  (available) => {
    if (!available) return
    const legacy = filterStates.isOpenDoctorStation
    if (legacy !== true && legacy !== false) return
    if (!filterStates.coverageStatus?.length) {
      filterStates.coverageStatus = legacy
        ? [CoverageStatus.OPEN]
        : [CoverageStatus.DOCTOR, CoverageStatus.UNIDENTIFIED]
    }
    filterStates.isOpenDoctorStation = null
  },
  { immediate: true }
)
const setCoverageStatus = (value: CoverageStatus[]) => {
  filterStates.coverageStatus = value.length ? value : null
  // D-B1: com chips, os filtros legados de vaga saem (os dois juntos fariam AND no BE).
  if (value.length) {
    filterStates.openedStation = null
    filterStates.isOpenDoctorStation = null
  }
}

const handleSubmit = async () => {
  if (!filterStates.startDate || !filterStates.endDate) {
    toast.warning('Selecione a data de início e fim antes de aplicar os filtros')
    return
  }

  isLoading.value = true
  // Tela sem chips (Aprovar plantões): nenhum coverageStatus residual viaja.
  if (!props.showCoverageStatus) filterStates.coverageStatus = undefined
  filterStates.startHour = undefined
  filterStates.endHour = undefined

  if (tempStartHour.value) {
    const val = String(tempStartHour.value)
    filterStates.startHour = val.includes(',')
      ? val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [val]
  }

  if (tempEndHour.value) {
    const val = String(tempEndHour.value)
    filterStates.endHour = val.includes(',')
      ? val
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : [val]
  }

  filterStates.crms = tempCrms.value
    ? tempCrms.value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : undefined
  filterStates.crmStates = tempCrmsUf.value
    ? tempCrmsUf.value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : undefined

  await useFilterStations.setFilters().finally(() => {
    isLoading.value = false
    handleToggleModal()
  })
}
const useFilterStations = useFilterStationsStore()

const handleClearFilters = async () => {
  handleToggleModal()
  await useFilterStations.clearFilters()
}

onUnmounted(() => {
  useFilterStations.clearFilters()
})

watch(
  () => modals.value[modalName],
  (isOpen) => {
    if (isOpen) loadOperationalGroups()
  },
  { immediate: true }
)
</script>

<style scoped>
.figma-custom-modal :deep(.modal-content) {
  border: 1px solid #d8d8e2 !important;
  border-radius: 8px !important;
  background-color: #ffffff !important;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.08) !important;
  align-items: flex-start !important;
}
.figma-custom-modal :deep(main) {
  width: 100%;
  padding: 0 !important;
  overflow: hidden !important;
}
</style>
